"use client";

import React, { useState, useEffect } from "react";
import { Search as SearchIcon, User, Menu as MenuIcon, Truck, FileText, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { CartIcon } from "@/components/shop/CartIcon";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function HeaderMenuConsumer({
  mobile = false
}: {
  mobile?: boolean;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="53620" className={`flex ${mobile ? "flex-col gap-4" : "gap-6"} animate-pulse`}>
        {[1, 2, 3].map(i => <div key={i} className="h-4 w-20 bg-muted rounded-md" />)}
      </div>;
  }
  if (mobile) {
    return <nav className="flex flex-col space-y-4">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-bold tracking-tight text-foreground hover:text-primary transition-colors" data-index={i}>
            {item.label}
          </Link>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-bold tracking-tight uppercase text-foreground/80 hover:text-primary transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function EcommerceProductsConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <p className="p-4 text-xs text-muted-foreground text-center font-medium">Loading okfun products...</p>;
  }
  if (error) {
    return <p className="p-4 text-xs text-destructive text-center font-medium">Unable to load products.</p>;
  }
  if (!products.length) {
    return <p className="p-4 text-xs text-muted-foreground text-center font-medium">No products found.</p>;
  }
  const visibleProducts = products.slice(0, 5);
  return <div className="grid gap-2 p-2">
      {visibleProducts.map((product, i) => <Link key={product.id} to={product.link} className="flex items-center gap-3 rounded-lg p-2 hover:bg-accent transition-colors group" data-index={i}>
          {product.featured_image_url ? <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-md bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover" loading="lazy" />
            </div> : <div className="h-10 w-10 flex-shrink-0 rounded-md bg-muted flex items-center justify-center">
              <SearchIcon className="h-4 w-4 text-muted-foreground" />
            </div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
              {product.title}
            </p>
            <div className="flex items-center gap-2">
              {product.price !== undefined && <span className="text-xs text-muted-foreground font-medium">
                  ${product.price}
                </span>}
            </div>
          </div>
        </Link>)}
    </div>;
}
function SearchBar() {
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  const [open, setOpen] = useState(false);
  return <EcommerceProductsProvider wp_query={wp_query} postType="product">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" className="w-full max-w-md flex items-center gap-3 px-4 py-2 h-10 rounded-full border border-border/60 bg-background/50 hover:border-primary/50 hover:bg-background transition-all duration-300 group justify-start shadow-sm" aria-label="Search okfun products">
            <SearchIcon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-sm text-muted-foreground/80 font-normal">Search okfun medical, massage, baby care...</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[320px] p-0 bg-background border-border shadow-xl rounded-xl overflow-hidden" align="center" sideOffset={8}>
          <div className="p-3 border-b border-border/50">
            <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Type to search okfun..." className="w-full" />
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {!hasSearchQuery ? <div className="p-8 text-center">
                <p className="text-xs text-muted-foreground font-medium">Start typing to find okfun products</p>
              </div> : <EcommerceProductsConsumer />}
          </div>
        </PopoverContent>
      </Popover>
    </EcommerceProductsProvider>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className="sticky top-0 z-50 w-full flex flex-col">
      {}
      <div className="w-full bg-secondary text-secondary-foreground py-1.5 px-4">
        <div className="container mx-auto max-w-7xl flex justify-between items-center text-[10px] sm:text-xs tracking-wider font-bold uppercase">
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline opacity-90">Premium Health & Wellness by okfun</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/WellnessGuidesPage" className="flex items-center gap-1.5 hover:text-primary transition-colors">
              <FileText className="h-3 w-3" />
              <span>Wellness Guides</span>
            </Link>
            <Link to="/ProfessionalMedicalPage" className="flex items-center gap-1.5 hover:text-primary transition-colors">
              <Truck className="h-3 w-3" />
              <span>Professional Medical</span>
            </Link>
          </div>
        </div>
      </div>

      {}
      <div className={`w-full border-b border-border/40 transition-all duration-400 ${isScrolled ? "bg-background/80 backdrop-blur-md shadow-md" : "bg-background"}`}>
        <div className="container mx-auto max-w-7xl px-4 h-20">
          <div className="h-full grid grid-cols-[auto_1fr_auto] items-center gap-4 lg:gap-8">
            
            {}
            <div className="flex items-center">
              <Link to="/HomePage" className="block shrink-0">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592322/img/okfun_logo.png" alt="okfun wellness" className="h-8 w-auto object-contain" />
              </Link>
            </div>

            {}
            <div className="hidden md:flex justify-center w-full">
              <SearchBar />
            </div>

            {}
            <div className="flex items-center justify-end gap-2 sm:gap-6">
              {}
              <div className="hidden lg:block">
                <MenuProvider menu_id="51">
                  <HeaderMenuConsumer />
                </MenuProvider>
              </div>

              {}
              <div className="flex items-center gap-3 pl-2 sm:pl-0">
                <Link to="#" className="p-2 text-foreground hover:text-primary transition-colors rounded-full hover:bg-accent/50" aria-label="My Account">
                  <User className="h-5 w-5" />
                </Link>
                
                <div className="relative">
                  <CartIcon className="text-foreground hover:text-primary transition-colors p-2 rounded-full hover:bg-accent/50" iconSize="size-5" variant="ghost" />
                </div>

                {}
                <Button variant="ghost" size="icon" className="lg:hidden ml-1 text-foreground hover:text-primary hover:bg-accent/50 rounded-full" onClick={() => setMobileMenuOpen(true)}>
                  <MenuIcon className="h-6 w-6" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="md:hidden border-b border-border/40 bg-background/95 backdrop-blur-sm px-4 py-3">
        <SearchBar />
      </div>

      {}
      {mobileMenuOpen && <div className="fixed inset-0 z-[60] bg-background/98 backdrop-blur-2xl lg:hidden animate-in fade-in slide-in-from-right duration-300">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b border-border/40">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592322/img/okfun_logo.png" alt="okfun wellness" className="h-8 w-auto" />
              <Button variant="ghost" size="icon" className="rounded-full hover:bg-accent/50" onClick={() => setMobileMenuOpen(false)}>
                <X className="h-6 w-6" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <MenuProvider menu_id="51">
                <HeaderMenuConsumer mobile />
              </MenuProvider>
              
              <div className="mt-8 pt-8 border-t border-border/40 space-y-4">
                <Link to="#" className="flex items-center gap-3 text-lg font-bold tracking-tight text-foreground/80 hover:text-primary transition-colors">
                  <User className="h-5 w-5" />
                  My Account
                </Link>
                <Link to="/WellnessGuidesPage" className="flex items-center gap-3 text-lg font-bold tracking-tight text-foreground/80 hover:text-primary transition-colors">
                  <FileText className="h-5 w-5" />
                  Wellness Guides
                </Link>
                <Link to="/ProfessionalMedicalPage" className="flex items-center gap-3 text-lg font-bold tracking-tight text-foreground/80 hover:text-primary transition-colors">
                  <Truck className="h-5 w-5" />
                  Professional Medical
                </Link>
              </div>
            </div>
          </div>
        </div>}
    </header>;
}